/** @jsxImportSource @emotion/react */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button, Icon, LabelGroup, Label, Loader } from 'semantic-ui-react';
import { useDebouncedCallback } from 'use-debounce';
import { scrollToTop, useCtrlEnterToRunCallback, useEscToRunCallback } from '../../../core/utils';
import { useAppDispatch } from '../../../core/store';
import { addEmptyGroup, fetchDistributionListGroups, selectFilter, selectFilteredDistributionGroups, selectFilters, selectIsFetchingDistributionListGroups, selectShowResults, setFilter, setFilters, setShowResults } from '../store/distributionListGroupSlice';
import { DistributionListGroupCard } from '../components/DistributionListGroupCard';
import style from './distributionListGroup.style';
import { SearchBox } from '../../../shared/SearchBox';
import { fetchKeringRegions, selectKeringRegions } from '../../users/store/keringRegionsSlice';
import { clearKeringCountries, fetchKeringCountries, selectIsFetchingKeringCountries, selectKeringCountries } from '../../users/store/keringCountriesSlice';
import { Country } from '../../../shared/model/country.model';
import { DistributionListGroupFilters, emptyDistributionListGroupFilters } from '../models/distributionListGroup';
import { toBrand } from '../../../shared/model/brand.model';
import { FilterBadge } from '../../../shared/FilterBadge';
import { CountryMultiSelectField } from '../components/CountryMultiSelectField';
import { BrandMultiSelectField } from '../components/BrandSelectField';
import { RegionMultiSelectField } from '../components/RegionMultiSelectField';

export const DistributionListGroups = (): JSX.Element => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const cardsContainerRef = useRef(null);
  const distributionListGroups = useSelector(selectFilteredDistributionGroups);
  const isLoading = useSelector(selectIsFetchingDistributionListGroups);
  const searchFilter = useSelector(selectFilter);
  const showFilters = !useSelector(selectShowResults);
  const setSearchBoxFilter = (filterValue: string) => {
    dispatch(setFilter(filterValue));
  };

  const setDebounceFilter = useDebouncedCallback(setSearchBoxFilter, 300);

  useEffect(() => {
    dispatch(fetchDistributionListGroups());
  }, [dispatch]);

  return (
    <div css={style.container}>
           {showFilters ? (
        <DistributionListGroupsFilters />
      ) : (
        <>
          <div css={style.header}>
            <Button
              css={style.addButton(t('distributionListGroups.addDistributionListGroup'))}
              icon='plus'
              onClick={() => {
                dispatch(addEmptyGroup());
                scrollToTop(cardsContainerRef.current);
              }}
            />
            <FiltersBadgesContainer />
            <SearchBox defaultValue={searchFilter} onChange={(filter: string) => setDebounceFilter(filter.trim())} />
          </div>
          {isLoading ? (
            <Loader active />
          ) : (
            <div css={style.distributionListGroupsList} ref={cardsContainerRef}>
              {distributionListGroups.map(group => (
                <DistributionListGroupCard distributionListGroup={group} key={group.id} />
              ))}
              {distributionListGroups.length === 0 && <DistributionListGroupNotFound />}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const DistributionListGroupsFilters = (): JSX.Element => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const filters = useSelector(selectFilters);
  const keringRegions = useSelector(selectKeringRegions);
  const keringCountries = useSelector(selectKeringCountries);
  const isFetchingKeringCountries = useSelector(selectIsFetchingKeringCountries);

  const [brands, setBrands] = useState<string[]>(filters.brands);
  const [regions, setRegions] = useState<string[]>(filters.regions);
  const [countries, setCountries] = useState<Country[]>(filters.countries);


  useEffect(() => {
    dispatch(fetchKeringRegions());
  }, [dispatch]);

  useEffect(() => {
    if (regions.length === 0) {
      dispatch(clearKeringCountries());
    } else {
      dispatch(fetchKeringCountries(regions));
    }
  }, [dispatch, regions]);

  const getFilters = useCallback(
    (): DistributionListGroupFilters => ({
      brands,
      regions,
      countries,
    }),
    [brands, regions, countries]
  );

  const search = useCallback(() => {
    dispatch(setFilters(getFilters()));
    dispatch(setShowResults(true));
  }, [dispatch, getFilters]);

  const clear = useCallback(() => {
    setBrands([]);
    setRegions([]);
    setCountries([]);
  }, []);

  useCtrlEnterToRunCallback(search);

  return (
    <div css={style.filtersContainer}>
      <div>
        <BrandMultiSelectField
          defaultValue={brands}
          onUpdate={selectedValues => {
            setBrands(selectedValues);
          }}
        />
        <RegionMultiSelectField
          regions={keringRegions}
          defaultValue={regions}
          onUpdate={selectedValues => {
            setRegions(selectedValues);
            setCountries([]);
          }}
        />
        <CountryMultiSelectField
          countries={keringCountries}
          editable={regions.length > 0}
          defaultValue={countries}
          onUpdate={selectedValues => {
            setCountries(selectedValues);
          }}
          loading={isFetchingKeringCountries}
        />
      </div>

      <div css={style.buttonsContainer}>
        <Button css={style.button} onClick={clear} icon='eraser' content={t('assessment.archive.clear')} />
        <Button css={style.button} onClick={search} icon='search' content={t('assessment.archive.search')} />
      </div>
    </div>
  );
};

const FiltersBadgesContainer = () => {
  const { t } = useTranslation();
  const filters = useSelector(selectFilters);
  const dispatch = useAppDispatch();
  const [numberOfFiltersApplied, setNumberOfFiltersApplied] = useState<number>(0);

  const getFiltersArray = useCallback(() => {
    const filtersBadges = {
      brands: filters.brands.map((b: string) => toBrand(b).description).join(', '),
      regions: filters.regions.join(', '),
      countries: filters.countries.map((country: Country) => country.description).join(', '),
    };

    return Object.keys(filtersBadges)
      .map(k => {
        const realValue = (filters as any)[k];

        return {
          show: !_.isEqual(realValue, (emptyDistributionListGroupFilters as any)[k]),
          field: k,
          description: (filtersBadges as any)[k],
          label: t(`users.${k}`),
        };
      })
      .filter(f => f.show);
  }, [filters, t]);

  const onFilterButtonClick = useCallback(() => {
    dispatch(setShowResults(false));
  }, [dispatch]);

  useEscToRunCallback(onFilterButtonClick);

  useEffect(() => {
    const n =
    (filters.brands.length > 0 ? 1 : 0) +
    (filters.regions.length > 0 ? 1 : 0) +
    (filters.countries.length > 0 ? 1 : 0);
    setNumberOfFiltersApplied(n);
  }, [filters]);

  return (
    <div css={style.badgeGroup}>
      <div css={style.badges}>
        <LabelGroup circular>
          {getFiltersArray().map(f => (
            <FilterBadge value={f.description} label={f.label} />
          ))}
        </LabelGroup>
      </div>
      <div css={style.filterButtonContainer}>
        <Button css={style.filterButton} onClick={onFilterButtonClick}>
          <div css={style.icon}>
            <Icon name='filter' />
          </div>
          <span>{t('assessment.archive.filtersText')}</span>
        </Button>
        {numberOfFiltersApplied > 0 && (
          <Label css={style.filterButtonBadge} floating circular>
            {numberOfFiltersApplied}
          </Label>
        )}
      </div>
    </div>
  );
};



const DistributionListGroupNotFound = (): JSX.Element => {
  const { t } = useTranslation();
  return (
    <div css={style.distributionListGroupNotFound}>
      <span>{t('distributionListGroups.distributionListGroupNotFound')}</span>
    </div>
  );
};